/**
 * Table row for a tutor or student in matching.
 * Click-to-expand is owned by the parent; this component renders row cells only.
 */

import * as React from 'react';
import { ChevronDown, ChevronRight, GraduationCap, User, Users, Handshake } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getProgramBadgeColor,
  LEVEL_BADGE_CONFIG,
  getLevelBadgeColor,
  MATCHING_BADGE_MAX_VISIBLE,
  PROGRAM_DISPLAY_CONFIG,
  SUBJECT_DISPLAY_CONFIG,
  getSubjectBadgeColor,
} from '@/components/pairs/pair-configs';
import type { TutorDashboardItem, StudentDashboardItem } from '@/types/remote/matching.types';
import type { Program } from '@/types/program.types';
import {
  deriveMatchingRowDisplay,
  getRemoteSubjectLabel,
  getTutorValorisationBadge,
} from '../matching-utils';

/** Shows up to {@link MATCHING_BADGE_MAX_VISIBLE} badges, then a +N tooltip for the rest. */
function BadgeList({
  items,
  renderBadge,
  tooltipLines,
}: Readonly<{
  items: unknown[];
  renderBadge: (item: unknown, index: number) => React.ReactNode;
  tooltipLines: string[];
}>) {
  const visible = items.slice(0, MATCHING_BADGE_MAX_VISIBLE);
  const overflow = items.length - MATCHING_BADGE_MAX_VISIBLE;

  return (
    <div className="flex flex-wrap gap-1 max-w-[200px]">
      {visible.map((item, i) => renderBadge(item, i))}
      {overflow > 0 && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex items-center justify-center rounded border px-1.5 py-0.5 text-xs font-medium cursor-default select-none"
                style={{
                  borderColor: 'color-mix(in srgb, var(--color-1-nuance-3) 30%, transparent)',
                  color: 'var(--muted-foreground)',
                  fontSize: '0.65rem',
                }}
              >
                +{overflow}
              </span>
            </TooltipTrigger>
            <TooltipContent variant="light" className="max-w-[220px]">
              <ul className="space-y-0.5 text-xs">
                {tooltipLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

/** Props for {@link MatchingRow}. */
interface MatchingRowProps {
  readonly data: TutorDashboardItem | StudentDashboardItem;
  readonly perspective: 'tutor' | 'student';
  readonly isExpanded?: boolean;
  readonly programsList: Program[];
}

/** Cells for one remote matching dashboard row (expand/collapse lives on the parent). */
export function MatchingRow({
  data,
  perspective,
  isExpanded = false,
  programsList,
}: MatchingRowProps) {
  const {
    genderConfig,
    subjectValue,
    capacityLabel,
    capacityColor,
    assignabilityConfig,
    partnerName,
    valorisation,
    programItems,
    levelItems,
    statusConfig,
  } = deriveMatchingRowDisplay(data, perspective, programsList);

  /** Tutor: engagement badge. Student: partner name. */
  function renderLastColumn(): React.ReactNode {
    if (perspective === 'tutor') {
      const badge = getTutorValorisationBadge(valorisation);
      return (
        <td>
          <LabelBadge
            label={badge.label}
            icon={badge.icon}
            variant="outlined"
            color={badge.color}
            size="sm"
          />
        </td>
      );
    }
    if (partnerName) {
      return (
        <td>
          <LabelBadge
            label={partnerName}
            icon={Handshake}
            variant="outlined"
            color="var(--color-3-nuance-1)"
            size="sm"
          />
        </td>
      );
    }
    return (
      <td>
        <span className="text-muted-foreground">—</span>
      </td>
    );
  }

  return (
    <>
      <td>
        <div className="flex items-center gap-2">
          <span
            className="shrink-0 transition-colors"
            style={{ color: 'var(--color-1-nuance-1)' }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLElement).style.color = 'var(--color-1-nuance-2)')
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLElement).style.color = 'var(--color-1-nuance-1)')
            }
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </span>
          {perspective === 'tutor' ? (
            <GraduationCap
              className="w-5 h-5 shrink-0"
              style={{ color: 'var(--color-1-nuance-1)' }}
            />
          ) : (
            <User className="w-5 h-5 shrink-0" style={{ color: 'var(--color-1-nuance-1)' }} />
          )}
          <span className="font-medium text-foreground">{data.fullName}</span>
        </div>
      </td>

      <td>
        {statusConfig ? (
          <LabelBadge
            label={statusConfig.label}
            icon={statusConfig.icon}
            variant={statusConfig.badgeVariant}
            color={statusConfig.badgeColor}
            size="sm"
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td>
        <LabelBadge
          label={assignabilityConfig.label}
          icon={assignabilityConfig.icon}
          variant={assignabilityConfig.badgeVariant}
          color={assignabilityConfig.badgeColor}
          size="sm"
        />
      </td>

      <td>
        {programItems.length > 0 ? (
          <BadgeList
            items={programItems}
            renderBadge={(item) => {
              const p = item as (typeof programItems)[0];
              return (
                <LabelBadge
                  key={p.programId}
                  label={p.displayName}
                  icon={PROGRAM_DISPLAY_CONFIG.icon}
                  variant={PROGRAM_DISPLAY_CONFIG.variant}
                  color={getProgramBadgeColor(p.displayName)}
                  size="sm"
                />
              );
            }}
            tooltipLines={programItems.map((p) => p.displayName)}
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td>
        <LabelBadge
          label={genderConfig.label}
          icon={genderConfig.icon}
          variant={genderConfig.badgeVariant}
          color={genderConfig.badgeColor}
          size="sm"
        />
      </td>

      <td>
        {subjectValue ? (
          <LabelBadge
            label={getRemoteSubjectLabel(subjectValue)}
            icon={SUBJECT_DISPLAY_CONFIG.icon}
            variant={SUBJECT_DISPLAY_CONFIG.variant}
            color={getSubjectBadgeColor(subjectValue)}
            size="sm"
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td>
        {levelItems.length > 0 ? (
          <BadgeList
            items={levelItems}
            renderBadge={(item) => {
              const level = item as (typeof levelItems)[0];
              return (
                <LabelBadge
                  key={level.levelId}
                  label={level.levelName}
                  icon={LEVEL_BADGE_CONFIG.icon}
                  variant="outlined"
                  color={getLevelBadgeColor(level.levelName)}
                  size="sm"
                />
              );
            }}
            tooltipLines={levelItems.map((l) => l.levelName)}
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      {perspective === 'tutor' && (
        <td>
          <LabelBadge
            label={capacityLabel}
            icon={Users}
            variant="outlined"
            color={capacityColor}
            size="sm"
          />
        </td>
      )}

      {renderLastColumn()}
    </>
  );
}
